import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { RecordDepositInput } from '@accounting/shared';
import { useDeposit, useRecordDeposit, useUpdateDeposit } from '../../hooks/useDeposits';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../accounts/AccountPicker';
import { ContactPicker } from '../contacts/ContactPicker';
import { ApiError } from '../../lib/api';
import { withDuplicateConfirm } from '../../lib/duplicateConfirm';
import { formatCurrency, todayIso } from '../../lib/formatters';

interface AllocRow {
  accountId: string;
  amount: string;
  memo: string;
  contactId: string;
}

interface Props {
  companyId: string;
  /** Seed from this deposit but record a new one (date reset). */
  copyFromId?: string;
  /** Edit this deposit in place. */
  editId?: string;
}

export function DepositEditor({ companyId, copyFromId, editId }: Props) {
  const navigate = useNavigate();
  const isEdit = !!editId;
  const sourceId = editId ?? copyFromId;

  const { data: accounts = [] } = useAccounts(companyId);
  const { data: source } = useDeposit(companyId, sourceId ?? '');
  const record = useRecordDeposit(companyId);
  const update = useUpdateDeposit(companyId);
  const mutation = isEdit ? update : record;

  const [bankAccountId, setBankAccountId] = useState('');
  const [depositDate, setDepositDate] = useState(todayIso());
  const [contactId, setContactId] = useState('');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<AllocRow[]>([{ accountId: '', amount: '', memo: '', contactId: '' }]);

  const bankAccounts = accounts.filter(
    (a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset' && a.accountSubtype === 'bank',
  );
  const arAccountId = accounts.find((a) => a.isActive && a.accountSubtype === 'accounts_receivable')?.id ?? '';

  useEffect(() => {
    if (!isEdit && !sourceId && !bankAccountId && bankAccounts.length > 0) {
      setBankAccountId(bankAccounts[0]!.id);
    }
  }, [bankAccounts, bankAccountId, isEdit, sourceId]);

  // Prefill empty lines with A/R for a fresh deposit.
  useEffect(() => {
    if (isEdit || sourceId || !arAccountId) return;
    setLines((prev) => prev.map((l) => (l.accountId ? l : { ...l, accountId: arAccountId })));
  }, [arAccountId, isEdit, sourceId]);

  // Seed from the source deposit (copy or edit).
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!sourceId || prefilledRef.current || !source) return;
    prefilledRef.current = true;
    setBankAccountId(source.bankAccountId);
    setContactId(source.contactId ?? '');
    setReference(source.reference ?? '');
    setMemo(source.memo ?? '');
    setLines(
      source.lines.map((l) => ({
        accountId: l.accountId,
        amount: String(parseFloat(l.amount)),
        memo: l.memo ?? '',
        contactId: '',
      })),
    );
    if (isEdit) setDepositDate(source.depositDate);
  }, [sourceId, source, isEdit]);

  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const totalStr = total.toFixed(2);

  const setLine = (i: number, patch: Partial<AllocRow>) =>
    setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addLine = () => setLines((prev) => [...prev, { accountId: arAccountId, amount: '', memo: '', contactId: '' }]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  if (isEdit && source && source.status !== 'outstanding') {
    return (
      <div>
        <Link to="/deposits/$id" params={{ id: editId! }} style={{ fontSize: 13 }}>
          ← Deposit
        </Link>
        <div className="card" style={{ padding: 20, marginTop: 8 }}>
          <p style={{ marginTop: 0 }}>
            This deposit can’t be edited because it is {source.status}
            {source.status === 'cleared' ? ' — un-clear it first if you need to change it.' : '.'}
          </p>
        </div>
      </div>
    );
  }

  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Could not save deposit'
      : mutation.error?.message;

  const valid = bankAccountId && total > 0 && lines.some((l) => l.accountId && parseFloat(l.amount) > 0);

  // "Save & New": record the deposit, then reset for the next one instead of navigating.
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  const resetForNext = () => {
    setContactId('');
    setReference('');
    setMemo('');
    setLines([{ accountId: arAccountId, amount: '', memo: '', contactId: '' }]);
    setFlash('Deposit saved — ready for the next one.');
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
    // The bank account and date are kept so several deposits are quick to record in a row.
  };

  const submit = (andNew = false) => {
    setFlash(null);
    const input: RecordDepositInput = {
      bankAccountId,
      depositDate,
      contactId: contactId || undefined,
      reference: reference.trim() || undefined,
      memo: memo.trim() || undefined,
      lines: lines
        .filter((l) => l.accountId && parseFloat(l.amount) > 0)
        .map((l) => ({
          accountId: l.accountId,
          amount: String(parseFloat(l.amount)),
          memo: l.memo.trim() || undefined,
          contactId: l.contactId || undefined,
        })),
    };
    if (isEdit && editId) {
      update.mutate({ id: editId, input }, { onSuccess: (d) => void navigate({ to: '/deposits/$id', params: { id: d.id } }) });
      return;
    }
    // Ask once if this looks like a deposit already recorded today.
    void withDuplicateConfirm(input, (i) => record.mutateAsync(i)).then((d) => {
      if (!d) return;
      if (andNew) resetForNext();
      else void navigate({ to: '/deposits/$id', params: { id: d.id } });
    });
  };

  const heading = isEdit ? 'Edit deposit' : copyFromId ? 'Copy deposit' : 'Record a deposit';

  return (
    <div>
      <Link to="/deposits" style={{ fontSize: 13 }}>← Deposit register</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>{heading}</h1>

      <div className="card" style={{ marginBottom: 16, padding: 16, maxWidth: 760 }}>
        <div className="form-grid">
          <label htmlFor="bank">Deposit into (bank)</label>
          <AccountPicker
            id="bank"
            accounts={accounts}
            value={bankAccountId}
            onChange={setBankAccountId}
            predicate={(a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset' && a.accountSubtype === 'bank'}
            placeholder="— select bank account —"
          />

          <label htmlFor="payer">Received from</label>
          <ContactPicker
            companyId={companyId}
            value={contactId}
            onChange={setContactId}
            defaultNewType="customer"
            placeholder="— optional (or name a customer per line) —"
            id="payer"
          />

          <label htmlFor="date">Date</label>
          <input id="date" type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)} style={{ maxWidth: 200 }} />

          <label htmlFor="ref">Reference</label>
          <input id="ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="(optional)" maxLength={50} style={{ maxWidth: 280 }} />

          <label htmlFor="memo">Memo</label>
          <input id="memo" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="(optional)" maxLength={200} />
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Allocation</h2>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          The bank account is debited for the total. Each line is credited (Accounts Receivable by
          default) — name a customer per line to record a batch deposit covering several payers.
        </p>
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Account</th>
              <th>Customer</th>
              <th>Memo</th>
              <th style={{ textAlign: 'right', width: 140 }}>Amount</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <AccountPicker
                    accounts={accounts}
                    value={l.accountId}
                    onChange={(id) => setLine(i, { accountId: id })}
                    predicate={(a) => a.isActive && a.allowDirectPosting}
                  />
                </td>
                <td>
                  <ContactPicker
                    companyId={companyId}
                    value={l.contactId}
                    onChange={(v) => setLine(i, { contactId: v })}
                    defaultNewType="customer"
                    placeholder="— optional —"
                    id={`dep-line-contact-${i}`}
                  />
                </td>
                <td>
                  <input value={l.memo} onChange={(e) => setLine(i, { memo: e.target.value })} style={{ width: '100%' }} />
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    value={l.amount}
                    onChange={(e) => setLine(i, { amount: e.target.value })}
                    style={{ width: '100%', textAlign: 'right' }}
                    placeholder="0.00"
                  />
                </td>
                <td>
                  <button className="btn-danger" onClick={() => removeLine(i)} disabled={lines.length <= 1}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={3}>Deposit total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(totalStr)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        <button className="btn-secondary" onClick={addLine}>+ Add line</button>

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}
        {flash && <div className="tag ok" style={{ marginTop: 12, display: 'inline-block' }}>{flash}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn-primary" onClick={() => submit()} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save deposit'}
          </button>
          {!isEdit && (
            <button className="btn-secondary" onClick={() => submit(true)} disabled={!valid || mutation.isPending}>
              Save &amp; New
            </button>
          )}
          <Link
            to={isEdit ? '/deposits/$id' : '/deposits'}
            params={isEdit ? { id: editId! } : undefined}
            className="btn-secondary"
            style={{ textDecoration: 'none' }}
          >
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}
